import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Timestamp Fields
 * Date and time tracking fields.
 *
 * `savedViewMembership` mirrors `../saved-views/ticket-saved-views.ts`.
 */
export const timestampFields = defineFieldConfigMap({
  // FIELD: created_at
  created_at: {
    type: "datetime",
    label: "Created",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    autoFill: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false, // Now editable with date picker
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    mobileDetailSectionId: "timeline",
    graphqlSelector: "created_at",
    savedViewMembership: [
      "all-tickets",
      "my-tickets",
      "no-time-entries",
      "with-time-entries",
    ],
  },

  // FIELD: updated_at
  updated_at: {
    type: "datetime",
    label: "Updated",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    sortable: true,
    filterable: true,
    resizable: true,
    autoFill: true,
    hideable: true,
    editable: false, // Now editable with date picker
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    groupable: true,
    groupingOptions: {
      dateGranularity: "month",
    },
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    mobileDetailSectionId: "timeline",
    graphqlSelector: "updated_at",
    /**
     * "stale" filter tokens: open tickets with no activity past a threshold —
     * the customer-service signal that someone may be sitting in the dark.
     * Mirrors the staleness cron grading (🟡 ≥24h, 🔴 ≥48h). Bundles the
     * unresolved-status constraint in one field's output (like `overdue`), so it
     * doesn't depend on cross-field filter merging. Any other value falls
     * through to the default date-range conversion.
     */
    toGraphQLFilter: (value, context) => {
      const hours = value === "stale48" ? 48 : value === "stale24" ? 24 : null;
      if (hours === null) return null;
      const cutoff = new Date(context.now.getTime() - hours * 60 * 60 * 1000);
      return {
        updated_at: { lt: cutoff.toISOString() },
        status: {
          in: [
            "new",
            "assigned",
            "in_progress",
            "waiting_for_customer",
            "waiting_for_vendor",
          ],
        },
      };
    },
  },

  // FIELD: resolved_at
  resolved_at: {
    type: "datetime",
    label: "Resolved",
    sortable: true,
    resizable: true,
    autoFill: true,
    hideable: true,
    editable: false, // Now editable with date picker
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "CheckCircle",
    mobileDetailSectionId: "timeline",
    graphqlSelector: "resolved_at",
    savedViewMembership: ["resolved-tickets"],
  },

  // FIELD: closed_at
  closed_at: {
    type: "datetime",
    label: "Closed",
    sortable: true,
    resizable: true,
    autoFill: true,

    hideable: true,
    editable: false, // Now editable with date picker
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "X",
    mobileDetailSectionId: "timeline",
    graphqlSelector: "closed_at",
  },
});
